import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Sparkles } from "lucide-react";
import { useLocation } from "react-router-dom";
import {
  deleteData,
  getBlobData,
  getData,
  postData,
} from "../../helpers/url-request";
import "./PiersAssistant.scss";

const AssistantContext = createContext(null);
const ASSISTANT_VISIBLE = false;
export function AskPiersButton() {
  const assistant = useContext(AssistantContext);
  if (!ASSISTANT_VISIBLE || !assistant?.enabled) return null;
  return (
    <button
      className="piers-assistant-header-button"
      type="button"
      onClick={assistant.openAssistant}
      aria-label="Open PIERS Assistant"
      aria-expanded={assistant.open}
    >
      <Sparkles size={18} strokeWidth={1.8} aria-hidden="true" />{" "}
      <span>Ask PIERS</span>
    </button>
  );
}
export function usePiersAssistantContext(value) {
  const assistant = useContext(AssistantContext);
  const serialized = JSON.stringify(value);
  useEffect(() => {
    if (!assistant) return undefined;
    const context = JSON.parse(serialized);
    assistant.publish(context);
    return () => assistant.publish(null);
  }, [assistant?.publish, serialized]);
}
export function contextFromLocation(pathname, search) {
  const positive = (value) =>
    /^\d+$/.test(value || "") && Number(value) > 0 ? Number(value) : undefined;
  const query = new URLSearchParams(search);
  if (pathname === "/inspections/list") return { screen: "InspectionCycles" };
  const journey = pathname.match(/^\/inspections\/(\d+)\/engagements\/(\d+)/);
  if (journey)
    return {
      screen: "Engagement",
      cycleId: positive(journey[1]),
      engagementId: positive(journey[2]),
    };
  const cycle = pathname.match(/^\/inspections\/(\d+)\/(preview|edit)/);
  const engagement = pathname.match(/^\/engagements\/(\d+)/);
  const review = pathname.match(/^\/inspectionreviews?\/(\d+)/);
  if (cycle) return { screen: "Cycle", cycleId: positive(cycle[1]) };
  if (engagement)
    return { screen: "Engagement", engagementId: positive(engagement[1]) };
  if (review) return { screen: "Review", reviewId: positive(review[1]) };
  return {
    screen: pathname.includes("inspectionreview") ? "Review" : "Dashboard",
    ...(positive(query.get("inspectionId"))
      ? { reviewId: positive(query.get("inspectionId")) }
      : {}),
  };
}
export const sameRecord = (a, b) => {
  const identity = (value) =>
    value?.checklistItemId
      ? `checklist:${value.reviewId}:${value.checklistItemId}`
      : value?.reviewId
      ? `review:${value.reviewId}`
      : value?.inspectionId
      ? `inspection:${value.inspectionId}`
      : value?.engagementId
      ? `engagement:${value.engagementId}`
      : value?.cycleId
      ? `cycle:${value.cycleId}`
      : value?.screen || "Dashboard";
  return identity(a) === identity(b);
};

const promptsFor = (context) =>
  context.screen === "InspectionCycles"
    ? [
        "How many inspection cycles are in progress?",
        "Summarize inspection cycle statuses",
        "Which cycles are past their end date?",
      ]
    : context.checklistItemId
    ? [
        "Summarize this item",
        "Check against firm policy",
        "Review available evidence",
        "Identify potential documentation gaps",
        "Draft a query",
        "Draft finding language",
      ]
    : context.reviewId || context.inspectionId
    ? [
        "Summarize this inspection",
        "What needs attention?",
        "Show incomplete checklist items",
        "Check against firm policy",
        "Summarize open queries",
        "Summarize findings",
      ]
    : [
        "Summarize this cycle",
        "What needs attention?",
        "Explain the recorded engagement selection reasons",
      ];

export default function PiersAssistantProvider({ children }) {
  const location = useLocation();
  const [published, publish] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [conversationContext, setConversationContext] = useState(null);
  const [error, setError] = useState("");
  const [conversations, setConversations] = useState([]);
  const [contextLabel, setContextLabel] = useState("");
  const abort = useRef(null);
  const operation = useRef(0);
  const end = useRef(null);
  const routeKey = `${location.pathname}${location.search}`;
  const current =
    published?.routeKey === routeKey
      ? published.context
      : contextFromLocation(location.pathname, location.search);
  const publisher = React.useCallback(
    (context) => publish(context ? { routeKey: routeKey, context } : null),
    [routeKey]
  );
  const openAssistant = React.useCallback(() => {
    if (ASSISTANT_VISIBLE) setOpen(true);
  }, []);
  const provider = useMemo(
    () => ({ publish: publisher, enabled, openAssistant, open }),
    [publisher, enabled, openAssistant, open]
  );
  // Changes are explicit: the old conversation stays bound; sending is disabled until a new conversation.
  const changed =
    conversationContext && !sameRecord(current, conversationContext);
  const currentKey = JSON.stringify(current);
  useEffect(() => {
    setContextLabel("");
    if (!open) return undefined;
    const controller = new AbortController();
    postData("/ai/context", JSON.parse(currentKey), controller.signal)
      .then((response) => {
        if (!controller.signal.aborted)
          setContextLabel(response.data.label || "");
      })
      .catch(() => {});
    return () => controller.abort();
  }, [open, currentKey]);
  useEffect(() => {
    if (!ASSISTANT_VISIBLE) return undefined;
    getData("/ai/status")
      .then((response) => setEnabled(Boolean(response?.data?.enabled)))
      .catch(() => setEnabled(false));
    return () => abort.current?.abort();
  }, []);
  useEffect(() => {
    if (open)
      getData("/ai/conversations")
        .then((response) => setConversations(response.data || []))
        .catch(() => {});
  }, [open, conversationId]);
  useEffect(() => {
    end.current?.scrollIntoView?.({ behavior: "smooth" });
  }, [messages, busy]);

  const stop = () => {
    operation.current++;
    abort.current?.abort();
    setBusy(false);
  };

  const newConversation = () => {
    stop();
    setMessages([]);
    setConversationId(null);
    setConversationContext(null);
    setError("");
    setMessage("");
  };

  const send = async (text = message) => {
    if (!text.trim() || busy || changed) return;
    const context = { ...current };
    setBusy(true);
    setError("");
    setMessage("");
    setMessages((items) => [
      ...items,
      { role: "user", narrative: text.trim() },
    ]);
    const request = new AbortController();
    abort.current = request;
    const generation = ++operation.current;
    if (!conversationContext) setConversationContext(context);
    try {
      const response = await postData(
        "/ai/chat",
        { message: text.trim(), conversationId, context },
        request.signal
      );
      if (generation !== operation.current) return;
      const answer = response.data;
      setConversationId(answer.conversationId);
      // Keep the screen context separately: backend canonical IDs need not exist in the route.
      setMessages((items) => [
        ...items,
        { ...answer, role: "assistant", id: answer.messageId },
      ]);
    } catch (reason) {
      if (generation === operation.current && reason.name !== "AbortError")
        setError(
          reason.message ||
            "PIERS Assistant is temporarily unavailable. Your inspection data has not been affected."
        );
    } finally {
      if (generation === operation.current) setBusy(false);
    }
  };

  const viewSource = async (item, source) => {
    if (!["Policy", "Evidence"].includes(source.type)) return;
    try {
      const blob = await getBlobData(
        `/ai/messages/${item.id}/sources/${encodeURIComponent(source.id)}`
      );
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = source.displayName;
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch {
      setError("This source is unavailable or you no longer have access.");
    }
  };

  const openDraft = async (action) => {
    if (
      changed ||
      current.reviewId !== action.reviewId ||
      current.checklistItemId !== action.checklistItemId
    )
      return;
    try {
      const response = await getData(`/ai/actions/${action.id}`);
      window.dispatchEvent(
        new CustomEvent("piers:assistant-draft", { detail: response.data })
      );
      setOpen(false);
    } catch {
      setError("This draft is unavailable or you no longer have access.");
    }
  };
  return (
    <AssistantContext.Provider value={provider}>
      <div
        className={`piers-assistant-layout${
          open ? " piers-assistant-layout--open" : ""
        }`}
      >
        {children}
        {open && (
          <aside
            className="piers-assistant piers-assistant--docked"
            aria-label="PIERS Assistant"
          >
            <header className="piers-assistant-panel-header">
              <h2>PIERS Assistant</h2>
              <button
                type="button"
                aria-label="Close PIERS Assistant"
                onClick={() => {
                  stop();
                  setOpen(false);
                }}
              >
                <i className="bx bx-x" />
              </button>
            </header>
            <div className="piers-assistant-panel-body">
              <p className="piers-assistant-subtitle">
                AI assistance for your inspection. Review suggestions before
                applying.
              </p>
              <section className="piers-assistant-context">
                <b>Current context</b>
                <span>
                  {contextLabel ||
                    `${current.screen}${
                      current.cycleId ? ` / Cycle ${current.cycleId}` : ""
                    }${
                      current.reviewId ? ` / Review ${current.reviewId}` : ""
                    }${
                      current.checklistItemId
                        ? ` / Checklist item ${current.checklistItemId}`
                        : ""
                    }`}
                </span>
              </section>
              <div className="piers-assistant-history">
                <button type="button" onClick={newConversation}>
                  New conversation
                </button>
                <select
                  aria-label="Private conversation history"
                  value={conversationId || ""}
                  disabled={busy}
                  onChange={async (event) => {
                    const id = event.target.value;
                    if (!id) return;
                    try {
                      const response = await getData(`/ai/conversations/${id}`);
                      newConversation();
                      setConversationId(id);
                      setMessages(response.data.messages);
                      setConversationContext(response.data.context);
                    } catch {
                      setError(
                        "This conversation is unavailable or you no longer have access."
                      );
                    }
                  }}
                >
                  <option value="">Private conversations</option>
                  {conversations.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.title}
                    </option>
                  ))}
                </select>
                {conversationId && (
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await deleteData(`/ai/conversations/${conversationId}`);
                        setConversations((items) =>
                          items.filter((item) => item.id !== conversationId)
                        );
                        newConversation();
                      } catch {
                        setError("Unable to delete this conversation.");
                      }
                    }}
                  >
                    Delete
                  </button>
                )}
              </div>
              {changed && (
                <div className="piers-assistant-context-change" role="status">
                  This conversation belongs to another context. Start a new
                  conversation to discuss the record currently on screen.
                  <button type="button" onClick={newConversation}>
                    Start here
                  </button>
                </div>
              )}
              <div
                className="piers-assistant-messages"
                aria-live="polite"
                aria-busy={busy}
              >
                {!messages.length && (
                  <div className="piers-assistant-prompts">
                    {promptsFor(current).map((text) => (
                      <button
                        type="button"
                        key={text}
                        onClick={() => send(text)}
                        disabled={busy}
                      >
                        {text}
                      </button>
                    ))}
                  </div>
                )}
                {messages.map((item, index) => (
                  <article key={item.id || index} className={`is-${item.role}`}>
                    <b>{item.role === "user" ? "You" : "PIERS Assistant"}</b>
                    <p>{item.narrative}</p>
                    {item.sources?.map((source) => (
                      <button
                        type="button"
                        key={source.id}
                        className="piers-assistant-source"
                        onClick={() => viewSource(item, source)}
                        disabled={!["Policy", "Evidence"].includes(source.type)}
                      >
                        {source.displayName}
                        {source.section ? ` · ${source.section}` : ""}
                        {source.page ? ` · Page ${source.page}` : ""}
                      </button>
                    ))}
                    {item.actions?.map((action) => (
                      <button
                        key={action.id}
                        type="button"
                        disabled={
                          changed ||
                          current.reviewId !== action.reviewId ||
                          current.checklistItemId !== action.checklistItemId
                        }
                        onClick={() => openDraft(action)}
                      >
                        {action.type === "DraftQuery"
                          ? "Review query draft"
                          : "Review finding draft"}
                      </button>
                    ))}
                  </article>
                ))}
                {busy && (
                  <p role="status">Reviewing authorized PIERS information…</p>
                )}
                <div ref={end} />
              </div>
              {error && (
                <p className="piers-assistant-error" role="alert">
                  {error}
                </p>
              )}
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  send();
                }}
              >
                <label htmlFor="piers-assistant-message">Ask PIERS</label>
                <textarea
                  id="piers-assistant-message"
                  value={message}
                  maxLength={4000}
                  rows={3}
                  onChange={(event) => setMessage(event.target.value)}
                  disabled={changed}
                  placeholder="Ask about this inspection or compare it with firm policy"
                />
                <div>
                  {busy ? (
                    <button type="button" onClick={stop}>
                      Stop
                    </button>
                  ) : (
                    <button type="submit" disabled={!message.trim() || changed}>
                      Send
                    </button>
                  )}
                  <small>
                    Private to you · Retained for 90 days after last activity
                  </small>
                </div>
              </form>
            </div>
          </aside>
        )}
      </div>
    </AssistantContext.Provider>
  );
}
